import { type ISavedView } from '@featit/shared/types/index';
import { listGenericViewSeedHeadings } from '@featit/shared/view-engine/generic-view-seed-headings';
import { DEFAULT_VIEW_ID_PREFIX } from './viewNames';

/**
 * I-1335 (`docs/concept-work-area-views-in-the-sidebar.md`, section I-1335): the built-in views stand
 * in the work area's view tree beside the custom views and folders, and move like them.
 *
 * A built-in that was moved has a placement row (`kind: 'builtin'`, its `seedId`, where it sits,
 * whether it is hidden); one never moved has none and stands first at the top level, in coded
 * order. In the tree both stand under the built-in's catalog id (`default-view:roadmap`), never the
 * placement's uuid: the id the address, the catalog and the server's order all already use.
 */

/** The id a row stands under in the tree and in an order: a placement by its built-in's catalog id. */
export function treeIdOf(row: Pick<ISavedView, 'id' | 'kind' | 'seedId'>): string {
  return row.kind === 'builtin' && row.seedId ? `${DEFAULT_VIEW_ID_PREFIX}${row.seedId}` : row.id;
}

/** The built-in a tree id names (`roadmap`), or null for a custom view or folder. */
export function builtInSeedIdOf(treeId: string): string | null {
  return treeId.startsWith(DEFAULT_VIEW_ID_PREFIX)
    ? treeId.slice(DEFAULT_VIEW_ID_PREFIX.length)
    : null;
}

/** A built-in never moved: no row, first at the top level, in coded order. */
function unplacedRow(
  projectId: string,
  seed: { id: string; name: string },
  position: number,
): ISavedView {
  return {
    id: `${DEFAULT_VIEW_ID_PREFIX}${seed.id}`,
    projectId,
    userId: '',
    name: seed.name,
    layout: 'list',
    filters: {},
    isShared: true,
    kind: 'builtin',
    seedId: seed.id,
    hidden: false,
    parentFolderId: null,
    position,
    createdAt: '',
    updatedAt: '',
  };
}

/**
 * Every built-in view, as the tree holds it: a placed one by its row, renamed to its tree id; an
 * unplaced one first, before any stored row (whose positions start at 0).
 */
export function builtInRows(projectId: string, rows: readonly ISavedView[]): ISavedView[] {
  const placements = new Map(
    rows.flatMap((row) => (row.kind === 'builtin' && row.seedId ? [[row.seedId, row]] : [])),
  );
  const seeds = listGenericViewSeedHeadings();
  return seeds.map((seed, index) => {
    const placement = placements.get(seed.id);
    return placement
      ? { ...placement, id: treeIdOf(placement), name: seed.name }
      : unplacedRow(projectId, seed, (index - seeds.length) * 10);
  });
}

/** The tree's rows: custom views, folders and every built-in that is not hidden. */
export function hierarchyRows(projectId: string, rows: readonly ISavedView[]): ISavedView[] {
  return [
    ...builtInRows(projectId, rows).filter((row) => !row.hidden),
    ...rows.filter((row) => row.kind !== 'builtin'),
  ];
}
